import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
  BriefAudienceType,
  BriefResponseDto,
  SocialChannelEnum,
} from '../../../../../../common/general-types/briefs.dto';
import { CreatorResponseDto } from '../../../../../../common/general-types/creators.dto';
import { FormatService } from '../../../../../../common/format/format.service';
import { ImageService } from '../../../../../../services/image/image.service';
import { PricingModelV2Service } from '../../../../../../services/pricing-model-v2/pricing-model-v2.service';
import { BriefApplicationService } from '../../../../../../services/brief-application/brief-application.service';
import { CentralManagementService } from '../../../../../../services/central-management/central-management.service';
import { PieceHireQuoteStore } from '../../../../../../services/brief-application/piece-hire-quote.store';
import { PaymentMethodTypeEnum } from '../../../../../../common/general-types/shout-plus.dto';
import { PieceHireQuote } from '../../../../../../common/general-types/piece-hire-quote.dto';
import { HasRoleDirectiveDirective } from '../../../../../../common/directives/has-role/has-role-directive.directive';
import { ApplicationStatusBadgeComponent } from '../../../../../../atoms/application-status-badge/application-status-badge.component';
import { RemunerationTypeIconComponent } from '../../../../../../shout-plus/atoms/remuneration-type-icon/remuneration-type-icon.component';
import { getResolvedPaymentMethodType, isShoutPlusBrief } from '../../../../../../shout-plus/utils/shout-plus-hire.util';
import { placementLabelForChannelId, socialChannelIdsOnPieces } from '../../../../../../common/brief-pieces/piece-rules';
import {
  NOTHING_OWED_COPY,
  pieceHireAction,
  quoteAudienceLabel,
  quotePieceLabel,
  toMajor,
} from '../../../../../../common/brief-pieces/piece-hire-quote';
import { briefCarriesAdLicense } from '../../../../../../common/brief-pieces/ad-license';
import { ManageBriefTabsService } from '../../../../service/manage-brief-tabs/manage-brief-tabs.service';
import { applyVat, shouldApplyVat, vatFor } from '../../hire-cost.util';
import { readChannels } from '../../cross-platform-state.util';
import { MbaIcon } from '../../atoms/mba-icon/mba-icon';
import { ui } from '../../assignment-ui';

/** How long a channel toggle waits before re-quoting, so quick clicks cost one request. */
const REQUOTE_DEBOUNCE_MS = 250;

/** What `findCreatorOpenApplications` actually returns: the application plus its brief and brand. */
interface OpenApplication {
  brandLogo: string;
  briefName: string;
  brandName: string;
  applicationStatus: ApplicationStatusEnum;
}

interface AudienceTier {
  title: string;
  bonusValue: number;
  followersFrom: number;
  followersTo: number;
}

/**
 * The hire for one Applied creator, inside the creator side panel: the price breakdown
 * (legacy lines, or the API quote for piece-list briefs), the channel toggles, the
 * audience-bonus tiers, and the Hire / Decline actions. Formerly the itemised-hire drawer.
 */
@Component({
  selector: 'mbt-hire-breakdown',
  imports: [CurrencyPipe, MbaIcon, HasRoleDirectiveDirective, ApplicationStatusBadgeComponent, RemunerationTypeIconComponent],
  templateUrl: './hire-breakdown.html',
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HireBreakdown {
  protected readonly ui = ui;
  protected readonly NOTHING_OWED_COPY = NOTHING_OWED_COPY;
  protected readonly toMajor = toMajor;
  protected readonly quotePieceLabel = quotePieceLabel;
  protected readonly quoteAudienceLabel = quoteAudienceLabel;
  protected readonly PaymentMethodTypeEnum = PaymentMethodTypeEnum;
  protected readonly line = 'flex items-baseline justify-between gap-4 py-2.5 text-sm';
  protected readonly lineLabel = 'text-(--fg-2)';
  protected readonly lineValue = 'font-semibold tabular-nums text-(--fg-1)';
  protected readonly winBox = 'rounded-lg border-l-4 border-(--success) bg-(--success)/10 px-3 py-2.5 text-sm text-(--fg-1)';
  protected readonly warnText = 'm-0 text-sm font-semibold text-(--warn)';

  public readonly imageService = inject(ImageService);
  public readonly formatService = inject(FormatService);
  private readonly pricingModelService = inject(PricingModelV2Service);
  private readonly briefApplicationService = inject(BriefApplicationService);
  private readonly centralManagement = inject(CentralManagementService);
  private readonly manageBriefTabsService = inject(ManageBriefTabsService);
  private readonly pieceQuotes = inject(PieceHireQuoteStore);

  readonly applicant = input.required<BriefApplicationResponseDto>();
  readonly brief = input<BriefResponseDto | undefined>(undefined);
  /** Expired applications show the price but can't be hired. */
  readonly disabled = input(false);
  /** Card heading and caption: the creator's next step, or why it's expired. */
  readonly heading = input('Review application');
  readonly caption = input('');
  readonly highlight = input(false);

  readonly hire = output<void>();
  readonly decline = output<void>();

  protected readonly creator = computed<CreatorResponseDto | undefined>(() => this.applicant().creator);
  protected readonly firstName = computed(() => this.creator()?.firstName?.trim() ?? '');

  /** Follows `actionsProcessing`, which stays set until the hire request finishes. */
  protected readonly isHiring = computed(() =>
    this.manageBriefTabsService.actionsProcessing().includes(this.applicant().id),
  );

  protected readonly paymentMethod = computed(() => this.manageBriefTabsService.paymentMethod());
  protected readonly resolvedPaymentType = computed(() => getResolvedPaymentMethodType(this.brief(), this.paymentMethod()));
  private readonly isShoutPlusCash = computed(
    () => isShoutPlusBrief(this.brief()) && this.resolvedPaymentType() === PaymentMethodTypeEnum.CASH,
  );
  /** SHOUT+ product, voucher or affiliate: nothing is paid in cash, so there are no price lines. */
  protected readonly isShoutPlusNonCash = computed(
    () => this.brief()?.brief_audience === BriefAudienceType.SHOUT_PLUS_CUSTOMERS
      && this.resolvedPaymentType() !== PaymentMethodTypeEnum.CASH,
  );
  private readonly totalPriceShoutPlus = computed(() => {
    const method = this.paymentMethod();
    return method?.type === PaymentMethodTypeEnum.CASH ? Number(method.value) / 100 : 0;
  });

  protected readonly currencyCode = computed(() => this.brief()?.currentPrice?.currencyCode?.toUpperCase() ?? 'USD');
  protected readonly videosPerCreator = computed(() => this.brief()?.videosPerCreator ?? 1);
  protected readonly socialPlatform = computed(() => this.brief()?.shoutBrief?.socialPlatform ?? null);

  /* ── Piece-list hire: priced by the API, never here ─────────────────── */

  protected readonly isPieceHire = computed(() => !!this.brief()?.pieces?.length);

  private readonly offeredPieceChannelIds = computed(() => {
    const pieces = this.brief()?.pieces;
    if (!pieces?.length) {
      return [] as number[];
    }
    const offered = new Set(readChannels(this.applicant().crossPlatformsPutForward));
    return socialChannelIdsOnPieces(pieces).filter((channelId) => offered.has(channelId));
  });

  /** Channels the brand has accepted, from the draft shared with the table's tiles. */
  private readonly acceptedPieceChannelIds = computed(() => {
    if (!this.isPieceHire()) {
      return [] as number[];
    }
    const offered = this.offeredPieceChannelIds();
    return this.centralManagement
      .crossPlatformDraftFor(this.applicant().id, offered)
      .filter((channelId) => offered.includes(channelId));
  });

  protected readonly offeredPieceChannels = computed(() => {
    const accepted = new Set(this.acceptedPieceChannelIds());
    return this.offeredPieceChannelIds().map((id) => ({ id, label: placementLabelForChannelId(id), accepted: accepted.has(id) }));
  });

  protected readonly unofferedPieceChannelLabels = computed(() => {
    const pieces = this.brief()?.pieces;
    if (!pieces?.length) {
      return [] as string[];
    }
    const offered = new Set(readChannels(this.applicant().crossPlatformsPutForward));
    return socialChannelIdsOnPieces(pieces)
      .filter((channelId) => !offered.has(channelId))
      .map((channelId) => placementLabelForChannelId(channelId));
  });

  /** The server quote for exactly the accepted channels, or null while it loads. */
  private readonly currentQuote = computed(() =>
    this.isPieceHire() ? this.pieceQuotes.quoteFor(this.applicant().id, this.acceptedPieceChannelIds()) : null,
  );

  /** The quote on screen. While a re-quote is on its way the previous one stays up, dimmed. */
  protected readonly pieceQuote = computed<PieceHireQuote | null>(() => {
    if (!this.isPieceHire()) {
      return null;
    }
    return (this.currentQuote() ?? this.pieceQuotes.lastQuoteFor(this.applicant().id))?.quote ?? null;
  });

  /** True while the figures on screen are not yet for the current channel choice. */
  protected readonly pieceQuoteStale = computed(() => this.isPieceHire() && !this.currentQuote());

  protected readonly pieceQuoteError = computed(() =>
    this.isPieceHire() ? this.pieceQuotes.entryFor(this.applicant().id, this.acceptedPieceChannelIds())?.error ?? null : null,
  );

  /** The hire refused with a new price; the brand confirms it by hiring again. */
  protected readonly piecePriceChanged = computed(
    () => this.isPieceHire() && this.pieceQuotes.priceChanged(this.applicant().id),
  );

  protected readonly pieceHireAction = computed(() => {
    const quote = this.currentQuote()?.quote;
    return quote ? pieceHireAction(quote) : null;
  });

  /** A piece hire can only go ahead on a current quote that owes something (F2). */
  protected readonly pieceHireBlocked = computed(
    () => this.isPieceHire() && (this.pieceHireAction() === null || this.pieceHireAction() === 'nothing-owed'),
  );

  /* ── Legacy (shoutBrief) lines ─────────────────────────────────────── */

  protected readonly postType = computed(() => {
    const platform = this.socialPlatform();
    if (platform != null) {
      switch (platform) {
        case SocialChannelEnum['TikTok Post']: return 'TikTok Post';
        case SocialChannelEnum['Instagram Reel']: return 'Instagram Reel';
        case SocialChannelEnum['Instagram Story']: return 'Instagram Story';
        case SocialChannelEnum['YouTube Shorts']: return 'YouTube Shorts';
        case SocialChannelEnum['Snapchat Spotlight']: return 'Snapchat Spotlight';
        case SocialChannelEnum['Snapchat Story']: return 'Snapchat Story';
        default: return 'Social Post';
      }
    }
    return briefCarriesAdLicense(this.brief()) ? 'Ad Content' : 'UGC Only';
  });

  protected readonly socialChannelName = computed(() => {
    const platform = this.socialPlatform();
    return platform != null ? SocialChannelEnum[platform] : '';
  });

  private readonly isAudienceBonusExcluded = computed(
    () => isShoutPlusBrief(this.brief()) && this.resolvedPaymentType() !== PaymentMethodTypeEnum.CASH,
  );

  protected readonly basePrice = computed(() => {
    const brief = this.brief();
    if (!brief) {
      return 0;
    }
    if (this.isShoutPlusCash()) {
      return this.totalPriceShoutPlus();
    }
    return this.pricingModelService.calculateBaseSpendCreateBrief(brief.currentPrice) / 100;
  });

  protected readonly primaryAudiencePrice = computed(() => {
    const brief = this.brief();
    const creator = this.creator();
    const platform = this.socialPlatform();
    if (this.isAudienceBonusExcluded() || !brief || !creator || platform == null) {
      return 0;
    }
    return this.pricingModelService.getAudiencePrice(platform, brief, creator);
  });

  /** The creator's followers exceed the brief's range, so the bonus was capped. */
  private readonly isAudienceBonusCapped = computed(() => {
    const brief = this.brief();
    const creator = this.creator();
    const platform = this.socialPlatform();
    if (this.isAudienceBonusExcluded() || !brief || !creator || platform == null) {
      return false;
    }
    return this.pricingModelService.isAudienceBonusCapped(platform, brief, creator);
  });

  private readonly appliedTier = computed(() => {
    const brief = this.brief();
    const creator = this.creator();
    const platform = this.socialPlatform();
    if (this.isAudienceBonusExcluded() || !brief || !creator || platform == null) {
      return null;
    }
    return this.pricingModelService.getAppliedAudienceTier(platform, brief, creator) ?? null;
  });

  private readonly usualTier = computed(() => {
    const brief = this.brief();
    const creator = this.creator();
    const platform = this.socialPlatform();
    if (!this.isAudienceBonusCapped() || !brief || !creator || platform == null) {
      return null;
    }
    return this.pricingModelService.resolveUsualAudienceBonus(platform, brief, creator).level ?? null;
  });

  protected readonly cappedTierTitle = computed(() => (this.isAudienceBonusCapped() ? this.appliedTier()?.title ?? null : null));

  protected readonly savingDetails = computed(() => {
    const brief = this.brief();
    const creator = this.creator();
    const platform = this.socialPlatform();
    if (this.isAudienceBonusExcluded() || !brief || !creator || platform == null) {
      return null;
    }
    const saving = this.pricingModelService.getAudienceBonusSaving(platform, brief, creator);
    if (!saving) {
      return null;
    }
    return {
      saving: saving.saving * this.videosPerCreator(),
      cappedTierTitle: saving.cappedLevel.title,
      usualTierTitle: saving.usualLevel.title,
      creatorFirstName: this.firstName() || 'This creator',
      possessivePronoun: creator.gender === 2 ? 'her' : creator.gender === 1 ? 'his' : 'their',
    };
  });

  /* ── Totals ─────────────────────────────────────────────────────────── */

  private readonly totalPrice = computed(() => {
    const brief = this.brief();
    if (!brief) {
      return 0;
    }
    if (this.isShoutPlusCash()) {
      return this.totalPriceShoutPlus();
    }
    if (this.isPieceHire()) {
      return toMajor(this.pieceQuote()?.brandNet);
    }
    const creator = this.creator();
    return creator ? this.pricingModelService.getTotalPrice(brief, creator) : 0;
  });

  /** The one VAT rule, shared with the Assignment table and the Review application modal. */
  protected readonly showVat = computed(() =>
    this.isPieceHire() ? (this.pieceQuote()?.vat ?? 0) > 0 : shouldApplyVat(this.brief(), this.paymentMethod()),
  );

  /** Piece hires show the server's VAT and total as quoted; nothing is recomputed here. */
  protected readonly vatAmount = computed(() =>
    this.isPieceHire() ? toMajor(this.pieceQuote()?.vat) : vatFor(this.totalPrice(), this.brief(), this.paymentMethod()),
  );

  protected readonly displayTotal = computed(() =>
    this.isPieceHire() && !this.isShoutPlusCash()
      ? toMajor(this.pieceQuote()?.brandTotal)
      : applyVat(this.totalPrice(), this.brief(), this.paymentMethod()),
  );

  /* ── Audience-bonus tiers ──────────────────────────────────────────── */

  /** Full pricing-model tiers when available; otherwise the brief's accepted tiers. */
  protected readonly audienceTiers = computed<AudienceTier[]>(() => {
    const brief = this.brief();
    const fullLevels = brief?.basePricingModel?.influencerLevels;
    if (fullLevels?.length) {
      return [...fullLevels].sort((a, b) => a.followersTo - b.followersTo);
    }
    return brief?.currentPrice?.influencerLevels ?? [];
  });

  protected readonly showAudienceTiers = computed(
    () => this.audienceTiers().length > 0
      && this.postType() !== 'UGC Only'
      && this.postType() !== 'Ad Content'
      && !this.isAudienceBonusExcluded(),
  );

  /* ── Admin: the creator's other open hires ─────────────────────────── */

  protected readonly openApplications = signal<OpenApplication[]>([]);
  protected readonly showOpenApplications = signal(false);

  private pieceQuoteRequestedFor: string | null = null;

  constructor() {
    // Re-quote whenever the accepted channels change. The first quote for a creator goes out at once.
    effect(() => {
      const applicationId = this.applicant().id;
      const accepted = this.acceptedPieceChannelIds();
      if (!this.isPieceHire()) {
        return;
      }
      const debounceMs = this.pieceQuoteRequestedFor === applicationId ? REQUOTE_DEBOUNCE_MS : 0;
      this.pieceQuoteRequestedFor = applicationId;
      untracked(() => this.pieceQuotes.request(applicationId, accepted, debounceMs));
    });

    // Other live briefs the creator is working on: hired through posted, on Public or Private briefs.
    effect((onCleanup) => {
      const uid = this.creator()?.blazeFirebaseUid;
      const briefId = this.brief()?.id;
      this.openApplications.set([]);
      this.showOpenApplications.set(false);
      if (!uid) {
        return;
      }
      const sub = this.briefApplicationService.findCreatorOpenApplications(uid, briefId).subscribe({
        next: (applications) => this.openApplications.set((applications ?? []) as unknown as OpenApplication[]),
        error: () => this.openApplications.set([]),
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  protected tierState(level: AudienceTier): 'applied' | 'usual' | 'outside' | 'plain' {
    if (sameTier(this.appliedTier(), level)) {
      return 'applied';
    }
    if (sameTier(this.usualTier(), level)) {
      return 'usual';
    }
    const required = this.brief()?.currentPrice?.influencerLevels;
    if (required?.length && !required.some((r: AudienceTier) => sameTier(r, level))) {
      return 'outside';
    }
    return 'plain';
  }

  protected togglePieceChannel(channelId: number): void {
    this.centralManagement.toggleCrossPlatformDraft(this.applicant().id, channelId, this.offeredPieceChannelIds());
  }

  protected confirmHire(): void {
    if (this.disabled() || this.isHiring() || this.pieceHireBlocked()) {
      return;
    }
    this.hire.emit();
  }
}

function sameTier(
  a: { title: string; followersFrom: number; followersTo: number } | null | undefined,
  b: { title: string; followersFrom: number; followersTo: number },
): boolean {
  return !!a && a.followersFrom === b.followersFrom && a.followersTo === b.followersTo && a.title === b.title;
}
